import { useState, useMemo, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Calendar as CalendarIcon } from 'lucide-react';
import { format } from 'date-fns';
import { CalendarDateMenu } from './CalendarDateMenu';
import type { CRMInteraction, CRMStakeholder, CRMFollowUp } from '@/lib/crm/types';
import { DayContentProps } from 'react-day-picker';

interface StakeholderCalendarProps {
  stakeholders: CRMStakeholder[];
  interactions: CRMInteraction[];
  followUps: CRMFollowUp[];
  selectedDate?: Date;
  onDateSelect?: (date: Date | undefined) => void;
  onAddInteraction?: (date: Date) => void;
  onAddFollowUp?: (date: Date) => void;
  onEditEvent?: (event: { type: 'interaction' | 'follow-up'; data: any }) => void;
}

export function StakeholderCalendar({ 
  stakeholders, 
  interactions,
  followUps,
  selectedDate: externalSelectedDate,
  onDateSelect, 
  onAddInteraction, 
  onAddFollowUp,
  onEditEvent
}: StakeholderCalendarProps) {
  const [internalSelectedDate, setInternalSelectedDate] = useState<Date | undefined>(new Date());
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date());
  const [menuDate, setMenuDate] = useState<Date | null>(null);
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [lastClickTime, setLastClickTime] = useState<number>(0);
  const [lastClickedDate, setLastClickedDate] = useState<string | null>(null);
  
  // Calculate separate counts for follow-ups and interactions
  const eventCounts = useMemo(() => {
    const followUpsMap = new Map<string, number>();
    const interactionsMap = new Map<string, number>();
    
    // Count follow-ups from followUps array
    followUps.forEach(f => {
      try {
        const dateKey = format(new Date(f.follow_up_date), 'yyyy-MM-dd');
        followUpsMap.set(dateKey, (followUpsMap.get(dateKey) || 0) + 1);
      } catch (e) {
        // Invalid date, skip
      }
    });
    
    // Count interactions
    interactions.forEach(i => {
      try {
        const dateKey = format(new Date(i.date), 'yyyy-MM-dd');
        interactionsMap.set(dateKey, (interactionsMap.get(dateKey) || 0) + 1);
      } catch (e) {
        // Invalid date, skip
      }
    });
    
    return { followUps: followUpsMap, interactions: interactionsMap };
  }, [followUps, interactions]);
  
  const handleDateClick = (date: Date) => {
    const now = Date.now();
    const dateKey = format(date, 'yyyy-MM-dd');
    
    // Always update selected date for filtering
    setInternalSelectedDate(date);
    onDateSelect?.(date);
    
    // Check if this is a double-click (< 300ms, same date)
    if (
      lastClickedDate === dateKey && 
      now - lastClickTime < 300
    ) {
      // Double-click detected → open popup menu
      setMenuDate(date);
      setIsMenuOpen(true);
      setLastClickTime(0); // Reset to prevent triple-click
      setLastClickedDate(null);
    } else {
      // Single click → just update filter (already done above)
      setLastClickTime(now);
      setLastClickedDate(dateKey);
    }
  };
  
  const handleAddInteraction = () => {
    setIsMenuOpen(false);
    if (menuDate) {
      onAddInteraction?.(menuDate);
    }
  };
  
  const handleAddFollowUp = () => {
    setIsMenuOpen(false);
    if (menuDate) {
      onAddFollowUp?.(menuDate);
    }
  };
  
  const handleGoToToday = () => {
    const today = new Date();
    setCurrentMonth(today);
    setInternalSelectedDate(today);
    onDateSelect?.(today);
  };
  
  // Custom day content component
  const DayContent = (props: DayContentProps) => {
    const dateKey = format(props.date, 'yyyy-MM-dd');
    const followUpCount = eventCounts.followUps.get(dateKey) || 0;
    const interactionCount = eventCounts.interactions.get(dateKey) || 0;
    const isMenuDay = menuDate && 
      format(props.date, 'yyyy-MM-dd') === format(menuDate, 'yyyy-MM-dd');
    
    // Get events for this date
    const dateFollowUps = followUps.filter(f => 
      format(new Date(f.follow_up_date), 'yyyy-MM-dd') === dateKey
    );
    const dateInteractions = interactions.filter(i => 
      format(new Date(i.date), 'yyyy-MM-dd') === dateKey
    );
    
    return (
      <CalendarDateMenu
        trigger={
          <div className="relative h-full w-full flex flex-col items-center justify-center">
            <button
              className="h-full w-full p-0 font-normal rounded-md"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                handleDateClick(props.date);
              }}
            >
              {props.date.getDate()}
            </button>
            
            {/* Badges for event counts */}
            {(followUpCount > 0 || interactionCount > 0) && (
              <div className="absolute bottom-0 left-0 right-0 flex gap-0.5 justify-center pb-1">
                {followUpCount > 0 && (
                  <Badge 
                    variant="default" 
                    className="h-3 px-1 text-[9px] leading-none"
                  >
                    {followUpCount}F
                  </Badge>
                )}
                {interactionCount > 0 && (
                  <Badge 
                    variant="secondary" 
                    className="h-3 px-1 text-[9px] leading-none"
                  >
                    {interactionCount}I
                  </Badge>
                )}
              </div>
            )}
          </div>
        }
        onAddInteraction={handleAddInteraction}
        onAddFollowUp={handleAddFollowUp}
        onEditEvent={onEditEvent}
        existingEvents={{
          followUps: dateFollowUps,
          interactions: dateInteractions
        }}
        open={isMenuOpen && isMenuDay}
        onOpenChange={(open) => {
          if (!open) {
            setIsMenuOpen(false);
            setMenuDate(null);
          }
        }}
      />
    );
  };
  
  return (
    <Card className="h-full flex flex-col">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2">
          <CardTitle className="text-base sm:text-lg">Lịch</CardTitle>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleGoToToday}
            className="w-full sm:w-auto gap-2"
          >
            <CalendarIcon className="h-4 w-4" />
            Hôm nay
          </Button>
        </div>
      </CardHeader>
      <CardContent className="p-2 sm:p-4 flex-1">
          <Calendar
          month={currentMonth}
          onMonthChange={setCurrentMonth}
          modifiers={{
            hasFollowUps: (date) => {
              const dateKey = format(date, 'yyyy-MM-dd');
              return eventCounts.followUps.has(dateKey);
            },
            hasInteractions: (date) => {
              const dateKey = format(date, 'yyyy-MM-dd');
              return eventCounts.interactions.has(dateKey);
            },
            selected: (date) => {
              const selectedDate = externalSelectedDate || internalSelectedDate;
              if (!selectedDate) return false;
              return format(date, 'yyyy-MM-dd') === format(selectedDate, 'yyyy-MM-dd');
            }
          }}
          modifiersClassNames={{
            hasFollowUps: 'font-medium',
            hasInteractions: 'font-medium',
            selected: 'bg-accent text-accent-foreground'
          }}
          components={{
            DayContent
          }}
          className="rounded-md border w-full"
        />
      </CardContent>
    </Card>
  );
}
